/* eslint no-undef: "warn" */
import React, { useEffect, useState } from 'react'

// Main Chakra UI components:
import {
    Box,
    Button,
    Center,
    HStack as ChakraHStack,
    Image,
    Link,
    List,
    ListItem,
    OrderedList,
    Text,
    UnorderedList,
    VStack,
} from '@chakra-ui/react'

import 'remixicon/fonts/remixicon.css'

import useMatchMedia from './MatchMedia'
import {
    actionColors,
    cursorColors,
    ActionBar,
    HStack,
    NotePane,
    SectionHeader,
    SubsectionHeader,
    RadialImage,
    RectImage,
    style,
    mapColors,
} from './PlayersGuideUtil'

const PlayersGuidePoweringUp = () => {
    const isDesktopResolution = useMatchMedia('(min-width:992px)', true)

    return (
        <VStack mt={style.sectionSpacing} spacing={style.defaultSpacing}>
            <Center h={'75px'}>
                <SectionHeader id="exploring-and-powering-up">Exploring the World</SectionHeader>
            </Center>
            <HStack>
                <Text style={{ width: isDesktopResolution ? '60%' : '100%' }}>
                    Mysterious Castle is a game without classes or levels. All character progression is achieved through
                    collecting equipment, reading books, drinking potions, or obtaining <i>blessings</i>. Items can be
                    taken from slain enemies, but most often you will find interesting items in <i>dungeons</i>. The
                    ruins of dungeons can be found scattered throughout the overworld, but the best items can be found
                    in underground dungeons, whose entrances can be found hidden in every corner of the overworld.
                    <br />
                    <br />
                    Books, which can either permanently increase a stat or skill; or teach a character a new spell, are
                    almost always found in dungeon libraries and shrines. The sooner you find a dungeon to explore, the
                    sooner you can power up your party!
                    <br />
                    <br />
                    The most powerful items in the game, are hidden in the deepest dungeons.
                </Text>
                <RectImage
                    src='/playersguide/treasure-vault.png'
                    alt='cast spell menu'
                    maxWidth={'40%'}
                    heading={'A treasure vault with an obelisk in it, a very lucky find!'}
                />
            </HStack>
            <NotePane style={{ width: '100%' }}>
                <i className='ri-information-line ri-xl'></i> Item based progression means that combat has no inherent
                benefits to you, unless it provides access to new items to collect. You must carefully weigh whether you
                want to spend the <i>hitpoints</i> and <i>mushrooms</i> necessary to defeat the foes before you.
                <br />
                <br />
                Mysterious Castle was designed this way in order to avoid disincentivizing non-combat gameplay, but as
                of yet, has not really provided a lot of alternatives to fighting. I hope to incorporate additional
                tactics such as pick-pocketing and negotiation to allow extremely varied gameplay styles in future
                versions.
            </NotePane>
            <SubsectionHeader id="finding-and-exploring-dungeons">Finding and Exploring Dungeons</SubsectionHeader>
            <HStack>
                <Text maxWidth={isDesktopResolution ? '60%' : '100%'}>
                    The overworld of Mysterious Castle is a treacherous forest maze filled with secret alcoves and
                    entrances to dungeons. These caves and groves often contain treasure, fruit, mushrooms, and stairs
                    leading downwards. The entrances to these areas are often deviously hidden behind trees. Your
                    party's characters can <i>spot</i> these entrances, spotted entrances are highlighted in{' '}
                    <b style={{ color: cursorColors.green }}>green</b>.
                    <br />
                    <br />
                    Characters will also <i>spot</i> containers with items in them, and corpses with special items.
                    <br />
                    <br />
                    The character with the highest <i>spot</i> skill in your party will automatically make these{' '}
                    <i>spot</i> checks as you explore, so remember to equip them with any spot improving items, and
                    train them with any books that improve the <i>spot</i> skill.
                </Text>
                <RectImage
                    src='/playersguide/hidden-grove.gif'
                    alt='dungeon entrance'
                    heading={'A secret grove is hidden behind this tree'}
                />
            </HStack>
            {/* <NotePane style={{ maxWidth: '50%' }}> */}
            <HStack>
                <RectImage src='/playersguide/world_map.gif' alt='world map' />
                <VStack maxWidth={isDesktopResolution ? '50%' : '100%'}>
                    <Text >
                        There are many routes and passages, and often they are obscure or even hidden. Your <i>world map</i> is an essential guide to navigation, and can often reveal alternative paths when you feel hemmed in by powerful enemies, or surrounded by impenetrable walls. 
                        <br/>
                        <br/>
                        The world map shows forested areas in <b style={{color: mapColors.green}}>green</b>, swamps in <b style={{color: mapColors.brown}}>brown</b>, cave walls in <b style={{color: mapColors.gray}}>gray</b>, doors in <b style={{color: mapColors.red}}>red</b>,
                        and stairs down to dungeons in <b style={{color: mapColors.purple}}>purple</b>. Your location is marked by a pulsing <b style={{color: mapColors.green}}>green</b> pip, and the locations
                        of <i>warp stones</i> are marked by pulsing <b style={{color: mapColors.yellow}}>yellow</b> pips.
                    </Text>
                    <NotePane>
                    <i className='ri-information-line ri-xl'></i> On Android and iOS you can pinch zoom in and out of the world map and pan to get a better view of your surroundings.
                    </NotePane>
                </VStack>
            </HStack>
            <HStack>
                <RectImage src='/playersguide/minimap-hidden.png' alt='minimap' />
                <Text maxWidth={isDesktopResolution ? '80%' : '100%'}>
                    You should also keep an eye out for inconsistencies in your <i>minimap</i> and <i>world map</i>. If
                    there is an unexplained gap in the treeline, then it's likely that the entrance of an alcove is
                    hidden there. If you feel stuck in a dead end, or surrounded by enemies too powerful to defeat, look
                    at your <i>world map</i> and see if you've passed by any suspicious looking areas.
                </Text>
            </HStack>
            {/* </NotePane> */}
            <NotePane>
                <i className='ri-information-line ri-xl'></i> Every spellcaster starts the game knowing the <i>vision</i>{' '}
                spell. With it you can peer behind dungeon walls and thick forest, but <u>not</u> cave walls. It can be
                a powerful way to find hidden alcoves and scout for enemies, but use it sparingly or you will run out of
                white mushrooms!
                <RectImage src='/playersguide/vision-spell.gif' alt='dungeon entrance' style={{ width: '100%' }} />
            </NotePane>
            <RectImage
                src='/playersguide/dungeon-entrance.png'
                alt='dungeon entrance'
                heading={'The entrance to an underground dungeon, or cave'} 
            />
            <Text>
                After some exploration you will find an enttrance to an underground dungeon or cave. There will usually be a small structure with some <i>stairs</i> leading down, and maybe a light source like a torch or lantern. Tap the stairs and select the <i>climb down</i> option. The game will <b>automatically save</b> your progress and then the screen will fade to black and quickly fade back in, with your party underground in a dungeon or cave. Sometimes you will find yourself alone in a spooky room, other times you'll land in the midst of enemies and combat will ensue. Occasionally you'll be in over your head against enemies you can't defeat, that's when you should open the <i>options screen</i> and select <i>reload before combat</i>, your party will reload at the top of the stairs, good as new and ready to find an alternate route or prepare an alternate strategy.
            </Text>
            <SubsectionHeader id="solo-mode">Splitting the Party: Going Solo</SubsectionHeader>
            <Text>
                You are out of mushrooms, your strongest fighter is gravely wounded, the way in front of you is 
                blocked by a horde vigilant enemies. What can you do? Time to quit... no way! Maybe it's time to split.
                At any time outside of combat, you can select characters from your party to control independently. You can 
                scout ahead with a sneaking rogue to find essential resources, send your fastest character to lure your 
                enemies into a trap, or position your team tactically before opening a dungeon door.
            </Text>
            <HStack>
                <VStack maxWidth={isDesktopResolution ? '50%' : '100%'}>
                    <Text>
                        In solo mode, your characters will no longer follow one another, you can freely switch
                        characters and move them independently. You can attach additional characters to <i>follow</i> the 
                        currently selected character by tapping on them and selecting the <i>follow</i> action,
                        creating a mini-party. Characters that are left behind stay put until you tell them to <i>follow</i> again.
                    </Text>
                    <RectImage 
                        src="/playersguide/solo_tactics.png" 
                        alt="party independently positioned" 
                        heading="Carefully positioning the fighters at the front, a sneaking rogue just out of sight, and the mage a safe distance away, before opening a door that might have danger behind it" />
                </VStack>
                <VStack>
                    <RectImage 
                        src="/playersguide/action_bar.png" 
                        alt="action bar with solo" 
                        heading="An action bar with the solo button in the middle" />
                    <RectImage 
                        src="/playersguide/action_bar_gather.png" 
                        alt="action bar with gather" 
                        heading="An action bar with the gather button in the middle" />
                    <NotePane>
                    <i className='ri-information-line ri-xl'></i> When you want to <i>gather</i> the party together just press 
                        the <i>gather</i> button in the action bar to have all characters follow the currently selected character, 
                        or manually tap each waiting character and select the <i>follow</i> action.
                    
                    You can only <i>gather</i> your party when they are all
                    in close proximity to one another. If one of your characters is too far away, they will not respond to
                    the <i>gather</i> action.
                </NotePane>
                </VStack>
                {/* <RectImage
                    src='/playersguide/hidden-grove.gif'
                    alt='dungeon entrance'
                    heading={'A secret grove is hidden behind this tree'}
                /> */}
            </HStack>
        </VStack>
    )
}

export default PlayersGuidePoweringUp
